import { useState, useEffect } from "react";
import Head from "next/head";
import useUser from "../lib/useUser";
import withSession from "../lib/session";
import { Layout } from "../common/Layout";
import { CalendarPublic } from "../common/CalendarPublic";
import {
  Box,
  Container,
  Paper,
  Grid,
  Button,
  Typography,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Tabs,
  Tab,
  Autocomplete,
  TextField,
  Table,
  TableContainer,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Link,
} from "@mui/material";

import {
  QueryClient,
  QueryClientProvider,
  useQuery,
  useMutation,
} from "react-query";
import { request, gql } from "graphql-request";

import { sortBy, toString, get, toInteger } from "lodash";
import { useDebounce } from "react-use";

import { toast } from "react-toastify";

import Moment from "react-moment";
import moment from "moment";
import "moment-timezone";

import { getMonth, getYear, setDate } from "date-fns";
import dayjs from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";
import customParseFormat from "dayjs/plugin/customParseFormat";
import isToday from "dayjs/plugin/isToday";

dayjs.extend(isToday);
dayjs.extend(customParseFormat);
dayjs.extend(localizedFormat);

const today = dayjs().startOf("day");

export default function PublicCalendar(props) {
  const { user } = props;

  const [yearAndMonth, setYearAndMonth] = useState([
    getYear(new Date()),
    getMonth(new Date()) + 1,
  ]);

  const { isLoading, error, data: calResults } = useQueryPublicCalendar({
    yearAndMonth,
  });

  // console.log("calResults:", calResults);

  return (
    <Layout>
      <Container>
        <Typography variant="h2" gutterBottom sx={{ textAlign2: "center" }}>
          Group Activities Calendar
        </Typography>
        <Typography variant="body1" paragraph>
          See our upcoming Join a Group and SurFit lessons, as well as other
          activities and events we host.
        </Typography>

        <Divider />
        <br />

        <div>
          {isLoading ? (
            <Box sx={{ textAlign: "center" }}>
              <CircularProgress />
              <br />
              Loading Calendar
            </Box>
          ) : (
            <CalendarPublic
              yearAndMonth={yearAndMonth}
              onYearAndMonthChange={setYearAndMonth}
              renderDay={(calendarDayObject) => (
                <CalendarDay
                  calendarDayObject={calendarDayObject}
                  user={user}
                  calResults={calResults}
                  // refetch={refetch}
                  // isLoading={isLoading}
                />
              )}
            />
          )}
        </div>
        <br />
      </Container>
    </Layout>
  );
}

const CalendarDay = ({
  calendarDayObject,
  calResults,
  user,
  refetch,
  isLoading,
}) => {
  const {
    dateString,
    dayOfMonth,
    isPreviousMonth,
    isCurrentMonth,
    isNextMonth,
    ...rest
  } = calendarDayObject;

  const date = dayjs(dateString, "YYYY-MM-DD", true);
  const isHistory = date.isBefore(today);
  const isToday = date.isToday();

  const lessonsToday =
    calResults?.lessons?.filter(
      (lesson) => lesson.metadata?.start_date === dateString
    ) ?? [];

  return (
    <Box
      sx={{
        height: "100%",
        minHeight: "120px",
        background: isToday ? "#eaf6ff" : isHistory ? "#e6e6e6" : null,
        // opacity: isHistory ? 0.4 : 1,
        // display: isCurrentMonth ? undefined : "none",
        // color: isLoading ? "red" : undefined,
      }}
    >
      <Box className="day-grid-item-header" sx={{ marginBottom: "8px" }}>
        <Box
          // onClick={toggleShowDayDialog}
          sx={{
            textAlign: "right",
          }}
        >
          <Box
            // onClick={toggleShowDayDialog}
            sx={{
              cursor: "pointer",
              display: "inline-block",
              padding: "4px",
              borderBottomLeftRadius: "4px",
              borderLeft: "1px solid #ddd",
              borderBottom: "1px solid #ddd",
            }}
          >
            {calendarDayObject.dayOfMonth}
          </Box>
          {isLoading ? " [loading...]" : null}
        </Box>
      </Box>
      {calResults?.dates?.[dateString]?.joinCount ? (
        <JoinCount
          dateString={dateString}
          value={calResults?.dates?.[dateString]?.joinCount}
        />
      ) : null}
      {calResults?.dates?.[dateString]?.surfitCount ? (
        <SurfitCount
          dateString={dateString}
          value={calResults?.dates?.[dateString]?.surfitCount}
        />
      ) : null}
      {/* {lessonsToday.map((lesson) => (
        <LessonOnCalendar
          key={lesson.id}
          calendarDayObject={calendarDayObject}
          lesson={lesson}
          user={user}
          refetch={refetch}
        />
      ))} */}
    </Box>
  );
};

const JoinCount = ({ dateString, value }) => {
  return (
    <Link
      color="inherit"
      underline="none"
      href={`/book/create?type=join&start_date=${dateString}`}
    >
      <Box
        sx={{
          m: "2px",
          padding: "2px",
          backgroundColor: "#2a70ab",
          color: "white",
          borderRadius: "4px",
        }}
      >
        <Grid
          container
          // columnSpacing={1}
          wrap="noWrap"
          sx={{}}
        >
          <Grid item sx={{ pl: 1, pr: 1 }}>
            <strong>{value}</strong>
          </Grid>
          <Grid item sx={{ flex: 1 }}>
            Join-a-Group
          </Grid>
          {/* <Grid item>join</Grid> */}
        </Grid>
      </Box>
    </Link>
  );
};

const SurfitCount = ({ dateString, value }) => {
  return (
    <Link
      color="inherit"
      underline="none"
      href={`/book/create?type=surfit&start_date=${dateString}`}
    >
      <Box
        sx={{
          m: "2px",
          padding: "2px",
          // pl: 1,
          // pr: 1,
          backgroundColor: "#2aab64",
          color: "white",
          borderRadius: "4px",
        }}
      >
        <Grid
          container
          // columnSpacing={1}
          wrap="noWrap"
          sx={{}}
        >
          <Grid item sx={{ pl: 1, pr: 1 }}>
            <strong>{value}</strong>
          </Grid>
          <Grid item sx={{ flex: 1 }}>
            SurFit Class
          </Grid>
          {/* <Grid item>join</Grid> */}
        </Grid>
      </Box>
    </Link>
  );
};

const LessonOnCalendar = ({ calendarDayObject, user, refetch, lesson }) => {
  return (
    <Box
      sx={{
        whiteSpace: "nowrap",
        width: "100%",
        overflow: "hidden",
        textOverflow: "ellipsis",
        margin: "2px",
        mb: 1,
        padding: "2px",
        // border: "1px solid #fafafa",
        // backgroundColor: "rgb(232,245,225)",
        backgroundColor: "#2a70ab",
        color: "white",
        borderRadius: "4px",
      }}
    >
      {lesson.title} sf sfdsf dsf sdfsd
    </Box>
  );
};

const useQueryPublicCalendar = ({ yearAndMonth }) => {
  // All the dates I can see
  // - for admin or pro
  // ["calendar", { yearAndMonth }]
  return useQuery(["calendar"], async () => {
    const result = await request(
      "/api/graphql",
      gql`
        query {
          publicCalendar {
            data
          }
        }
      `,
      {
        data: {},
      }
    );
    // console.log("Public Calendar Result:", result);
    return result.publicCalendar.data;
  });
};

const PrettyDate = ({ yearMonthDate, format }) => {
  // yesterday, today, tomorrow
  const date = moment(yearMonthDate).startOf("day").valueOf();
  if (date == moment().startOf("day").valueOf())
    return "Today: " + moment(yearMonthDate).format(format);
  if (date == moment().add(1, "day").startOf("day").valueOf())
    return "Tomorrow: " + moment(yearMonthDate).format(format);
  return moment(yearMonthDate).format(format);
};

export const getServerSideProps = withSession(async function ({ req, res }) {
  const user = req.session.get("user") ?? null;

  // if (!user) {
  //   return {
  //     redirect: {
  //       destination: "/login",
  //       permanent: false,
  //     },
  //   };
  // }

  return {
    props: { user },
  };
});
